import type { InfiniteData, QueryClient } from '@tanstack/vue-query'
import type { PortalChatConversation, PortalChatMessage, PortalChatMessagePage, PortalChatUnreadSummary } from '@/api/portalMessaging'

export interface PortalChatUpdate {
  type: 'chat.updated'
  conversationId: string
  message?: PortalChatMessage
  conversation?: PortalChatConversation
  unreadCount?: number
  readReceipt?: PortalChatReadReceipt | null
}

export interface PortalChatReadReceipt {
  throughCreatedAt: string
  throughMessageId: string
}

// Socket moderation and recipient reads may arrive before older HTTP responses.
export function freshestMessage(previous: PortalChatMessage | undefined, incoming: PortalChatMessage): PortalChatMessage {
  if (previous && (previous.version ?? 0) > (incoming.version ?? 0)) {
    incoming = { ...previous, read: previous.read || incoming.read }
  }
  if (previous && previous.deliveryStatus !== 'pending' && incoming.deliveryStatus === 'pending') return previous
  if (previous?.replyTo && previous.replyTo.id === incoming.replyTo?.id && (previous.replyTo.version ?? 0) > (incoming.replyTo?.version ?? 0)) {
    incoming = { ...incoming, replyTo: previous.replyTo }
  }
  return previous?.read && incoming.mine && (!incoming.deliveryStatus || incoming.deliveryStatus === 'sent')
    ? { ...incoming, read: true } : incoming
}

function withUpdatedReply(message: PortalChatMessage, source: PortalChatMessage): PortalChatMessage {
  const reply = message.replyTo
  if (!reply || reply.id !== source.id || (reply.version ?? 0) > (source.version ?? 0)) return message
  return { ...message, replyTo: {
    id: source.id, authorName: source.authorName, authorType: source.authorType,
    body: source.deletedAt ? null : source.body, hasAttachments: !source.deletedAt && source.attachments.length > 0,
    version: source.version, deletedAt: source.deletedAt,
  } }
}

const matches = (message: PortalChatMessage, search: string) => (message.body ?? '').toLocaleLowerCase('ru').includes(search.trim().toLocaleLowerCase('ru'))
// PostgreSQL keeps microseconds; Date.parse alone would mark a later message in
// the same millisecond as read based on its UUID rather than its delivery time.
const subMillis = (value: string) => (value.match(/\.(\d+)(?:Z|[+-]\d{2}:?\d{2})$/)?.[1] ?? '').padEnd(9, '0').slice(3)
const compare = (a: Pick<PortalChatMessage, 'createdAt' | 'id'>, b: Pick<PortalChatMessage, 'createdAt' | 'id'>) =>
  Date.parse(a.createdAt) - Date.parse(b.createdAt) || subMillis(a.createdAt).localeCompare(subMillis(b.createdAt)) || a.id.localeCompare(b.id)
const receiptPosition = (receipt: PortalChatReadReceipt) => ({ createdAt: receipt.throughCreatedAt, id: receipt.throughMessageId })

function withReceipt(message: PortalChatMessage, receipt?: PortalChatReadReceipt): PortalChatMessage {
  if (!receipt || !message.mine || message.read || message.deliveryStatus && message.deliveryStatus !== 'sent') return message
  return compare(message, receiptPosition(receipt)) <= 0 ? { ...message, read: true } : message
}

function withPageReceipt(page: PortalChatMessagePage, receipt?: PortalChatReadReceipt): PortalChatMessagePage {
  if (!receipt) return page
  return { ...page, messages: page.messages.map(message => withReceipt(message, receipt)) }
}

export function upsertChatMessage(current: InfiniteData<PortalChatMessagePage> | undefined, incoming: PortalChatMessage, search = ''): InfiniteData<PortalChatMessagePage> | undefined {
  if (!current) return search || !matches(incoming, search) ? current : { pages: [{ messages: [incoming], nextCursor: null }], pageParams: [null] }
  const old = current.pages.flatMap(page => page.messages).find(message => message.id === incoming.id)
  const message = freshestMessage(old, incoming)
  // A quote may be visible even when its original is outside the loaded history.
  current = { ...current, pages: current.pages.map(page => ({ ...page,
    messages: page.messages.map(item => withUpdatedReply(item, message)),
  })) }
  const index = current.pages.findIndex(page => page.messages.some(item => item.id === message.id))
  const first = current.pages[0]
  if (!first) return current
  const earliest = first.messages[0]
  // Do not splice unseen old messages into the latest page: that would skip history.
  if (index < 0 && first.nextCursor && earliest && compare(message, earliest) < 0) return current
  const target = index >= 0 && old?.createdAt === message.createdAt ? index : 0
  const pages = current.pages.map(page => ({ ...page, messages: page.messages.filter(item => item.id !== message.id) }))
  if (matches(message, search)) pages[target]!.messages = [...pages[target]!.messages, message].sort(compare)
  return { ...current, pages }
}

function upsertConversation(current: PortalChatConversation[], conversation: PortalChatConversation): PortalChatConversation[] {
  return [...current.filter(item => item.id !== conversation.id), conversation]
    .sort((a, b) => Date.parse(b.lastMessageAt ?? b.updatedAt) - Date.parse(a.lastMessageAt ?? a.updatedAt) || a.id.localeCompare(b.id))
}

/** Overlay socket events received while an HTTP snapshot was in flight. */
export class PortalChatCache {
  private revision = 0
  private updates: { revision: number; update: PortalChatUpdate }[] = []
  // Weak references retain provenance without keeping old conversations in memory.
  private liveMessages = new WeakSet<PortalChatMessage>()
  private receipts = new Map<string, PortalChatReadReceipt>()
  private applying = 0
  constructor(private client: QueryClient, readonly account: string) {}

  apply(update: PortalChatUpdate): void {
    this.updates.push({ revision: ++this.revision, update })
    if (this.updates.length > 500) this.updates.shift()
    const receipt = update.readReceipt
    const previous = this.receipts.get(update.conversationId)
    if (receipt && (!previous || compare(receiptPosition(previous), receiptPosition(receipt)) < 0)) {
      this.receipts.set(update.conversationId, receipt)
      ++this.applying
      try {
        this.client.setQueriesData<InfiniteData<PortalChatMessagePage>>({ queryKey: ['portal', 'chat-messages', this.account, update.conversationId] }, current =>
          current ? { ...current, pages: current.pages.map(page => withPageReceipt(page, receipt)) } : current)
      } finally { --this.applying }
    }
    if (update.message) this.accept(update.message)
    if (update.conversation) this.client.setQueryData<PortalChatConversation[]>(['portal', 'chats', this.account], current => current ? upsertConversation(current, update.conversation!) : undefined)
    if (typeof update.unreadCount === 'number') this.client.setQueryData(['portal', 'chat-unread', this.account], { unreadCount: update.unreadCount })
  }

  accept(incoming: PortalChatMessage): void {
    const known = [...this.updates].reverse().find(entry => entry.update.message?.id === incoming.id)?.update.message
    const message = withReceipt(freshestMessage(known, incoming), this.receipts.get(incoming.conversationId))
    this.liveMessages.add(message)
    const prefix = ['portal', 'chat-messages', this.account, message.conversationId]
    const queries = this.client.getQueryCache().findAll({ queryKey: prefix })
    ++this.applying
    try {
      for (const query of queries) {
        this.client.setQueryData<InfiniteData<PortalChatMessagePage>>(query.queryKey, current => current ? upsertChatMessage(current, message, String(query.queryKey[4] ?? '')) : undefined)
      }
    } finally { --this.applying }
  }

  /** InfiniteQuery commits every loaded page at once. A per-page HTTP overlay cannot
   * protect its earlier pages while an older page is still loading. Merge at that
   * final commit too, including live rows whose journal entries have been evicted. */
  shareMessages(previous: unknown, incoming: unknown, conversationId: string, search: string): unknown {
    // Our own single-message writes already start from the current cache. Only an
    // external HTTP snapshot needs the more expensive whole-history overlay.
    if (this.applying) return incoming
    const current = incoming as InfiniteData<PortalChatMessagePage> | undefined
    const old = previous as InfiniteData<PortalChatMessagePage> | undefined
    if (!current?.pages) return incoming
    let result = current
    const candidates = new Map<string, PortalChatMessage>()
    for (const page of old?.pages ?? []) for (const message of page.messages) {
      if (message.conversationId === conversationId && (message.read || this.liveMessages.has(message) ||
        (message.version ?? 0) > 0 || (message.replyTo?.version ?? 0) > 0)) candidates.set(message.id, message)
    }
    for (const { update } of this.updates) {
      if (update.conversationId === conversationId && update.message) {
        candidates.set(update.message.id, freshestMessage(candidates.get(update.message.id), update.message))
      }
    }
    for (const message of candidates.values()) result = upsertChatMessage(result, message, search)!
    // Preserve provenance if an HTTP copy replaced the same live row, so a later
    // slow page cannot roll it back after the bounded journal has moved on.
    for (const page of result.pages) for (const message of page.messages) {
      if (candidates.has(message.id)) this.liveMessages.add(message)
    }
    const receipt = this.receipts.get(conversationId)
    return receipt ? { ...result, pages: result.pages.map(page => withPageReceipt(page, receipt)) } : result
  }

  async messages(load: () => Promise<PortalChatMessagePage>, conversationId: string, cursor: string | null, search: string): Promise<PortalChatMessagePage> {
    const { revision, snapshot } = await this.snapshot(load)
    let page = snapshot
    for (const { update } of this.updates.filter(entry => entry.revision > revision)) {
      if (!update.message || update.conversationId !== conversationId) continue
      const old = page.messages.find(message => message.id === update.message!.id)
      if (cursor && !old) {
        page = { ...page, messages: page.messages.map(message => withUpdatedReply(message, update.message!)) }
        continue
      }
      page = upsertChatMessage({ pages: [page], pageParams: [cursor] }, update.message, search)!.pages[0]!
    }
    return withPageReceipt(page, this.receipts.get(conversationId))
  }

  async conversations(load: () => Promise<PortalChatConversation[]>): Promise<PortalChatConversation[]> {
    const { revision, snapshot } = await this.snapshot(load)
    let result = snapshot
    for (const { update } of this.updates.filter(entry => entry.revision > revision)) if (update.conversation) result = upsertConversation(result, update.conversation)
    return result
  }

  async unread(load: () => Promise<PortalChatUnreadSummary>): Promise<PortalChatUnreadSummary> {
    const { revision, snapshot } = await this.snapshot(load)
    const newest = [...this.updates].reverse().find(entry => entry.revision > revision && typeof entry.update.unreadCount === 'number')
    return newest ? { unreadCount: newest.update.unreadCount! } : snapshot
  }

  private async snapshot<T>(load: () => Promise<T>): Promise<{ revision: number; snapshot: T }> {
    for (;;) {
      const revision = this.revision
      const snapshot = await load()
      // A slow first load has no existing cached rows to protect evicted events.
      // Reload canonically if the bounded journal can no longer cover its lifetime.
      // The caller keeps the same AbortSignal, including across these retries.
      if (!this.updates.length || this.updates[0]!.revision <= revision + 1) return { revision, snapshot }
    }
  }
}
